import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth }     from '../context/AuthContext';
import { useEquipo }   from '../context/EquipoContext';
import { getDesafiosEquipo, getEquipos, getJugadores, getPartidosEquipo } from '../services/api';

function formatFecha(fechaStr) {
  if (!fechaStr) return '';
  try {
    const solo = String(fechaStr).slice(0, 10);
    const [y, m, d] = solo.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return fechaStr; }
}

function getResultado(partido, esLocal, esFutbol) {
  // Para padel/tenis con resultado_detalle
  if (!esFutbol && partido.resultado_detalle) {
    try {
      const det = typeof partido.resultado_detalle === 'string'
        ? JSON.parse(partido.resultado_detalle)
        : partido.resultado_detalle;
      if (det?.sets && Array.isArray(det.sets)) {
        const setsStr = det.sets.map(s => `${s.local}-${s.visitante}`).join(', ');
        const setsLocal = det.sets.filter(s => s.local > s.visitante).length;
        const setsVisit = det.sets.filter(s => s.visitante > s.local).length;
        const gane = esLocal ? setsLocal > setsVisit : setsVisit > setsLocal;
        return { marcador: setsStr, gane, empate: false, esSets: true };
      }
    } catch {}
  }
  // Fútbol o sin detalle
  const gP = esLocal ? (partido.goles_local ?? partido.resultado_local ?? 0) : (partido.goles_visitante ?? partido.resultado_visitante ?? 0);
  const gR = esLocal ? (partido.goles_visitante ?? partido.resultado_visitante ?? 0) : (partido.goles_local ?? partido.resultado_local ?? 0);
  return { marcador: `${gP} — ${gR}`, gane: gP > gR, empate: gP === gR, esSets: false };
}

function getIniciales(nombre) {
  if (!nombre) return '??';
  return nombre.split(' ').filter(p => p.length > 0).slice(0,2).map(p => p[0].toUpperCase()).join('');
}

function getColoresEquipo(id) {
  const pares = [
    ['#EF4444','#B91C1C'], ['#1A6BFF','#0F4FCC'],
    ['#16A34A','#166534'], ['#7C3AED','#5B21B6'],
    ['#FF6B1A','#CC5214'], ['#F59E0B','#D97706'],
  ];
  return pares[(id || 0) % pares.length];
}

function partidoYaJugado(fecha, hora) {
  if (!fecha || !hora) return false;
  const solo = String(fecha).slice(0, 10);
  const [hh, mm] = hora.split(':').map(Number);
  const fechaPartido = new Date(`${solo}T${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}:00-03:00`);
  return new Date() >= fechaPartido;
}

export default function Partidos() {
  const navigate        = useNavigate();
  const { token }       = useAuth();
  const { equipo, deporteActivo } = useEquipo();
  const esFutbol = !deporteActivo || deporteActivo === 'futbol';

  const [desafios,  setDesafios]  = useState([]);
  const [partidos,  setPartidos]  = useState([]);
  const [equipos,   setEquipos]   = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [cargando,  setCargando]  = useState(false);

  const [modalConvocatoria, setModalConvocatoria] = useState(null);
  const [seleccionados,     setSeleccionados]     = useState([]);
  const [yaEvaluo,          setYaEvaluo]          = useState({});

  useEffect(() => {
    if (!equipo?.id || !token) return;
    setCargando(true);
    Promise.all([
      getDesafiosEquipo(equipo.id, token),
      getEquipos(),
      getJugadores(equipo.id),
      getPartidosEquipo(equipo.id, token),
    ]).then(async ([desafiosData, equiposData, jugadoresData, partidosData]) => {
      if (Array.isArray(desafiosData)) setDesafios(desafiosData);
      if (Array.isArray(equiposData))  setEquipos(equiposData);
      if (Array.isArray(jugadoresData)) setJugadores(jugadoresData);
      if (Array.isArray(partidosData)) {
        setPartidos(partidosData);
        const API = process.env.REACT_APP_API_URL;
        const checks = await Promise.all(
          partidosData.map(p =>
            fetch(`${API}/api/evaluaciones/verificar/${p.id}/${equipo.id}`)
              .then(r => r.json())
              .then(d => ({ partidoId: p.id, yaEvaluo: d.yaEvaluo || false }))
              .catch(() => ({ partidoId: p.id, yaEvaluo: false }))
          )
        );
        const mapa = {};
        checks.forEach(({ partidoId, yaEvaluo }) => { mapa[partidoId] = yaEvaluo; });
        setYaEvaluo(mapa);
      }
    }).catch(() => {})
    .finally(() => setCargando(false));
  }, [equipo, token]);

  const getNombreEquipo = (id) => {
    const e = equipos.find(eq => eq.id === id);
    return e?.nombre || 'Equipo #' + id;
  };

  const confirmados = desafios.filter(d =>
    (d.equipo_local_id === equipo?.id || d.equipo_visitante_id === equipo?.id) &&
    (d.estado === 'aceptado' || d.estado === 'confirmado')
  );

  const pendientes = desafios.filter(d =>
    (d.equipo_local_id === equipo?.id || d.equipo_visitante_id === equipo?.id) &&
    d.estado === 'pendiente'
  );

  const getRivalId = (d) => d.equipo_local_id === equipo?.id
    ? d.equipo_visitante_id
    : d.equipo_local_id;

  const abrirConvocatoria = (desafio) => {
    setModalConvocatoria(desafio);
    setSeleccionados(jugadores.map(j => j.id));
  };

  const toggleJugador = (id) => {
    setSeleccionados(prev =>
      prev.includes(id) ? prev.filter(j => j !== id) : [...prev, id]
    );
  };

  const enviarConvocatoria = () => {
    const d        = modalConvocatoria;
    const rivalId  = getRivalId(d);
    const rival    = getNombreEquipo(rivalId);
    const fecha    = formatFecha(d.fecha);
    const hora     = d.hora || '';
    const nombreEq = equipo?.nombre || 'Mi equipo';
    const grupoWsp = equipo?.grupo_wsp;
    const mensaje  = encodeURIComponent(
      `⚽ CONVOCATORIA — ${nombreEq}\n\n` +
      `Partido vs ${rival}\n` +
      `📅 ${fecha} · ${hora}\n` +
      `📍 Por confirmar\n\n` +
      `Confirmen asistencia respondiendo este mensaje.\n` +
      `Los espero a todos 💪\n\n` +
      `— Enviado desde TuRival`
    );
    if (grupoWsp) {
      window.open(grupoWsp, '_blank');
    } else {
      window.open(`https://wa.me/?text=${mensaje}`, '_blank');
    }
    setModalConvocatoria(null);
  };

  if (cargando) {
    return (
      <div style={styles.pagina}>
        <div style={styles.header}>
          <div style={styles.titulo}>Mis partidos</div>
        </div>
        <div style={styles.cargando}>Cargando partidos...</div>
      </div>
    );
  }

  return (
    <div style={styles.pagina}>

      <div style={styles.header}>
        <div style={styles.titulo}>Mis partidos</div>
        <div style={styles.sub}>
          {partidos.length} jugados · {confirmados.length} confirmados · {pendientes.length} pendientes
        </div>
      </div>

      <div style={styles.contenido}>

        {/* CONFIRMADOS */}
        {confirmados.length > 0 && (
          <div style={styles.seccion}>
            <div style={styles.seccionTitulo}>Próximos confirmados</div>
            {confirmados.map(d => {
              const rivalId   = getRivalId(d);
              const nombre    = getNombreEquipo(rivalId);
              const [c1, c2]  = getColoresEquipo(rivalId);
              const partidoRegistrado = partidos.find(p => p.desafio_id === d.id);
              const yaEvaluoEste = partidoRegistrado ? yaEvaluo[partidoRegistrado.id] : false;
              return (
                <div key={d.id} style={styles.card}>
                  <div style={styles.cardInner}>
                    <div style={{ ...styles.crest, background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                      {getIniciales(nombre)}
                    </div>
                    <div style={styles.cardInfo}>
                      <div style={styles.cardRival}>vs {nombre}</div>
                      <div style={styles.cardFecha}>📅 {formatFecha(d.fecha)} · {d.hora}</div>
                      {(d.recinto_nombre || d.recinto_manual) && (
                        <div style={styles.cardFecha}>🏟️ {d.recinto_manual || d.recinto_nombre}{d.cancha_nombre ? ` · ${d.cancha_nombre}` : ''}</div>
                      )}
                      {partidoRegistrado && (() => {
                        const esLocal = d.equipo_local_id === equipo?.id;
                        const gP = esLocal ? partidoRegistrado.goles_local : partidoRegistrado.goles_visitante;
                        const gR = esLocal ? partidoRegistrado.goles_visitante : partidoRegistrado.goles_local;
                        const gane   = gP > gR;
                        const empate = gP === gR;
                        return (
                          <div style={{
                            marginTop: 6,
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontSize: 18, fontWeight: 800,
                            color: gane ? '#16A34A' : empate ? '#F59E0B' : '#EF4444',
                          }}>
                            {gP} — {gR} · {gane ? '✓ Victoria' : empate ? '= Empate' : '✗ Derrota'}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ ...styles.estadoBadge, background: '#F0FDF4', color: '#16A34A' }}>
                      ✓ Confirmado
                    </div>
                  </div>
                  <div style={styles.cardBotones}>
                    {esFutbol && (
                      <button style={styles.btnConvocar} onClick={() => abrirConvocatoria(d)}>
                        📣 Convocar jugadores
                      </button>
                    )}
                    {yaEvaluoEste ? (
                      <div style={{ fontSize: 11, color: '#16A34A', fontFamily: "'Barlow', sans-serif", display: 'flex', alignItems: 'center' }}>
                        ✓ Resultado y evaluación completados
                      </div>
                    ) : partidoRegistrado ? (
                      <button
                        style={styles.btnEvaluar}
                        onClick={() => navigate('/app/post-partido', {
                          state: {
                            desafio: { ...d, desafio_id: d.id },
                            soloEvaluar: true
                          }
                        })}
                      >
                        ⭐ Evaluar rival
                      </button>
                    ) : partidoYaJugado(d.fecha, d.hora) ? (
                      <button
                        style={styles.btnPostPartido}
                        onClick={() => navigate('/app/post-partido', { state: { desafio: d } })}
                      >
                        Registrar resultado →
                      </button>
                    ) : (
                      <button
                        style={{ ...styles.btnPostPartido, opacity: 0.4, cursor: 'not-allowed' }}
                        disabled
                      >
                        ⏳ Partido aún no jugado
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* PENDIENTES */}
        {pendientes.length > 0 && (
          <div style={styles.seccion}>
            <div style={styles.seccionTitulo}>Desafíos pendientes</div>
            {pendientes.map(d => {
              const rivalId  = getRivalId(d);
              const nombre   = getNombreEquipo(rivalId);
              const [c1, c2] = getColoresEquipo(rivalId);
              return (
                <div key={d.id} style={styles.card}>
                  <div style={styles.cardInner}>
                    <div style={{ ...styles.crest, background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                      {getIniciales(nombre)}
                    </div>
                    <div style={styles.cardInfo}>
                      <div style={styles.cardRival}>vs {nombre}</div>
                      <div style={styles.cardFecha}>📅 {formatFecha(d.fecha)} · {d.hora}</div>
                      {d.mensaje && <div style={styles.cardMensaje}>"{d.mensaje}"</div>}
                    </div>
                    <div style={{ ...styles.estadoBadge, background: '#FFF7ED', color: '#FF6B1A' }}>
                      ⏳ Pendiente
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* HISTORIAL */}
        {partidos.length > 0 && (
          <div style={styles.seccion}>
            <div style={styles.seccionTitulo}>Historial</div>
            {partidos.map(p => {
              const esLocal  = p.equipo_local_id === equipo?.id;
              const rivalId  = esLocal ? p.equipo_visitante_id : p.equipo_local_id;
              const nombre   = getNombreEquipo(rivalId);
              const [c1, c2] = getColoresEquipo(rivalId);
              const { marcador, gane, empate, esSets } = getResultado(p, esLocal, esFutbol);
              const yaEvaluoEste = yaEvaluo[p.id];
              return (
                <div key={p.id} style={styles.card}>
                  <div style={styles.cardInner}>
                    <div style={{ ...styles.crest, background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                      {getIniciales(nombre)}
                    </div>
                    <div style={styles.cardInfo}>
                      <div style={styles.cardRival}>vs {nombre}</div>
                      <div style={styles.cardFecha}>📅 {formatFecha(p.fecha)} · {p.hora}</div>
                      {(p.recinto_display || p.recinto_nombre) && (
                        <div style={styles.cardFecha}>🏟️ {p.recinto_display || p.recinto_nombre}{(p.cancha_display || p.cancha_nombre) ? ` · ${p.cancha_display || p.cancha_nombre}` : ''}</div>
                      )}
                      {esSets && (
                        <div style={{ fontSize: 10, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif", marginTop: 2 }}>Sets: {marcador}</div>
                      )}
                    </div>
                    <div style={styles.resultadoWrap}>
                      <div style={{
                        ...styles.marcador,
                        fontSize: esSets ? 14 : 28,
                        color: gane ? '#16A34A' : empate ? '#F59E0B' : '#EF4444',
                      }}>
                        {esSets
                          ? (gane ? '🏆 Victoria' : '✗ Derrota')
                          : marcador}
                      </div>
                      <div style={{
                        ...styles.resultadoBadge,
                        background: gane ? '#F0FDF4' : empate ? '#FFFBEB' : '#FEF2F2',
                        color: gane ? '#16A34A' : empate ? '#F59E0B' : '#EF4444',
                      }}>
                        {gane ? '✓ Victoria' : empate ? '= Empate' : '✗ Derrota'}
                      </div>
                    </div>
                  </div>
                  {!yaEvaluoEste && (
                    <div style={{ marginTop: 10, textAlign: 'right' }}>
                      <button
                        style={styles.btnEvaluar}
                        onClick={() => navigate('/app/post-partido', {
                          state: {
                            desafio: { ...p, desafio_id: p.desafio_id, id: p.desafio_id },
                            soloEvaluar: true,
                          }
                        })}
                      >
                        ⭐ Evaluar rival
                      </button>
                    </div>
                  )}
                  {yaEvaluoEste && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#16A34A', fontFamily: "'Barlow', sans-serif", textAlign: 'right' }}>
                      ✓ Ya evaluaste este partido
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {confirmados.length === 0 && pendientes.length === 0 && partidos.length === 0 && (
          <div style={styles.vacio}>
            <div style={styles.vacioIcono}>⚽</div>
            <div style={styles.vacioTitulo}>Sin partidos aún</div>
            <div style={styles.vacioSub}>
              Desafía a otro equipo para empezar a jugar y acumular ELO.
            </div>
            <button style={styles.btnBuscar} onClick={() => navigate('/app/matchmaking')}>
              Buscar rival →
            </button>
          </div>
        )}

      </div>

      {/* Modal convocatoria */}
      {modalConvocatoria && (
        <div style={styles.modalOverlay} onClick={() => setModalConvocatoria(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitulo}>📣 Convocar jugadores</div>
                <div style={styles.modalSub}>
                  vs {getNombreEquipo(getRivalId(modalConvocatoria))} · {formatFecha(modalConvocatoria.fecha)} · {modalConvocatoria.hora}
                </div>
              </div>
              <button style={styles.btnCerrar} onClick={() => setModalConvocatoria(null)}>✕</button>
            </div>

            <div style={styles.mensajePreview}>
              <div style={styles.mensajePreviewLabel}>Mensaje a enviar</div>
              <div style={styles.mensajeBubble}>
                ⚽ CONVOCATORIA — {equipo?.nombre}<br/>
                Partido vs {getNombreEquipo(getRivalId(modalConvocatoria))}<br/>
                📅 {formatFecha(modalConvocatoria.fecha)} · {modalConvocatoria.hora}<br/>
                Los espero a todos 💪<br/>
                — Enviado desde TuRival
              </div>
            </div>

            <div style={styles.jugadoresList}>
              <div style={styles.jugadoresLabel}>Jugadores ({seleccionados.length}/{jugadores.length})</div>
              {jugadores.map(j => (
                <div
                  key={j.id}
                  style={{
                    ...styles.jugadorItem,
                    background: seleccionados.includes(j.id) ? '#F0FDF4' : '#F4F6FA',
                  }}
                  onClick={() => toggleJugador(j.id)}
                >
                  <div style={{
                    ...styles.jugadorCheck,
                    background: seleccionados.includes(j.id) ? '#16A34A' : '#E8ECF4',
                  }}>
                    {seleccionados.includes(j.id) && <span style={{ color: 'white', fontSize: 11 }}>✓</span>}
                  </div>
                  <div style={styles.jugadorNombre}>{j.nombre}</div>
                  {j.celular && <div style={styles.jugadorCelular}>{j.celular}</div>}
                </div>
              ))}
            </div>

            {jugadores.length === 0 && (
              <div style={styles.sinJugadores}>
                No tienes jugadores en la nómina.
                <span
                  style={styles.linkNomina}
                  onClick={() => { setModalConvocatoria(null); navigate('/app/nomina'); }}
                >
                  Agregar jugadores →
                </span>
              </div>
            )}

            <div style={styles.modalBotones}>
              <button style={styles.btnCancelarModal} onClick={() => setModalConvocatoria(null)}>
                Cancelar
              </button>
              <button style={styles.btnEnviarWsp} onClick={enviarConvocatoria}>
                📲 Enviar por WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  pagina: { minHeight: '100vh', background: '#F4F6FA' },
  header: {
    background: '#0D1117', padding: '24px 32px',
    borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 24,
  },
  titulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 32, fontWeight: 800, color: 'white', marginBottom: 4,
  },
  sub: { fontSize: 13, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  cargando: {
    padding: '60px', textAlign: 'center',
    color: '#9BA5B7', fontSize: 13, fontFamily: "'Barlow', sans-serif",
  },
  contenido: { padding: '0 32px 32px' },
  seccion: { marginBottom: 28 },
  seccionTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 14, fontWeight: 700, color: '#9BA5B7',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
  },
  card: {
    background: 'white', border: '1px solid #E8ECF4',
    borderRadius: 14, padding: '16px 20px', marginBottom: 10,
  },
  cardInner: { display: 'flex', alignItems: 'center', gap: 14 },
  crest: {
    width: 44, height: 44, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 14, fontWeight: 800, color: 'white',
  },
  cardInfo: { flex: 1 },
  cardRival: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 18, fontWeight: 800, color: '#141922', marginBottom: 3,
  },
  cardFecha: { fontSize: 12, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginBottom: 2 },
  cardMeta: { fontSize: 11, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif" },
  cardMensaje: { fontSize: 11, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif", fontStyle: 'italic' },
  estadoBadge: {
    display: 'inline-block', fontSize: 11, fontWeight: 700,
    padding: '4px 10px', borderRadius: 20,
    fontFamily: "'Barlow', sans-serif", flexShrink: 0,
  },
  resultadoWrap: { textAlign: 'right', flexShrink: 0 },
  marcador: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 28, fontWeight: 800, lineHeight: 1, marginBottom: 4,
  },
  resultadoBadge: {
    fontSize: 10, fontWeight: 700, padding: '3px 8px',
    borderRadius: 20, fontFamily: "'Barlow', sans-serif",
    display: 'inline-block',
  },
  cardBotones: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 },
  btnConvocar: {
    padding: '10px', background: '#F0FDF4', border: '1.5px solid #86EFAC',
    borderRadius: 10, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 13, fontWeight: 700, color: '#16A34A',
  },
  btnPostPartido: {
    padding: '10px', background: '#FF6B1A', border: 'none',
    borderRadius: 10, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 13, fontWeight: 700, color: 'white',
  },
  btnRevancha: {
    background: '#FEF2F2', border: '1.5px solid #FCA5A5',
    borderRadius: 8, padding: '7px 14px',
    fontSize: 12, fontWeight: 700, color: '#EF4444',
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
  },
  btnEvaluar: {
    background: '#FFF7ED', border: '1.5px solid #FED7AA',
    borderRadius: 8, padding: '7px 14px',
    fontSize: 12, fontWeight: 700, color: '#FF6B1A',
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
  },
  vacio: { textAlign: 'center', padding: '60px 20px' },
  vacioIcono: { fontSize: 48, marginBottom: 16 },
  vacioTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 24, fontWeight: 800, color: '#141922', marginBottom: 8,
  },
  vacioSub: {
    fontSize: 14, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif",
    maxWidth: 360, margin: '0 auto 20px', lineHeight: 1.6,
  },
  btnBuscar: {
    background: '#FF6B1A', color: 'white', border: 'none',
    borderRadius: 10, padding: '11px 24px', cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15, fontWeight: 700,
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: 'white', borderRadius: 20, padding: 28,
    width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: 20,
  },
  modalTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 22, fontWeight: 800, color: '#141922', marginBottom: 3,
  },
  modalSub: { fontSize: 12, color: '#6B7280', fontFamily: "'Barlow', sans-serif" },
  btnCerrar: {
    background: '#F4F6FA', border: 'none', borderRadius: 8,
    width: 32, height: 32, fontSize: 14, color: '#6B7280',
    cursor: 'pointer', flexShrink: 0,
  },
  mensajePreview: { marginBottom: 20 },
  mensajePreviewLabel: {
    fontSize: 10, fontWeight: 700, color: '#9BA5B7',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 8, fontFamily: "'Barlow', sans-serif",
  },
  mensajeBubble: {
    background: '#DCF8C6', borderRadius: '12px 12px 12px 2px',
    padding: '12px 16px', fontSize: 13,
    fontFamily: "'Barlow', sans-serif", lineHeight: 1.6, color: '#141922',
  },
  jugadoresList: { marginBottom: 16 },
  jugadoresLabel: {
    fontSize: 11, fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 8, fontFamily: "'Barlow', sans-serif",
  },
  jugadorItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    borderRadius: 10, padding: '10px 12px', marginBottom: 6,
    cursor: 'pointer', transition: 'all 0.1s',
  },
  jugadorCheck: {
    width: 20, height: 20, borderRadius: 5,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  jugadorNombre: {
    flex: 1, fontSize: 13, fontWeight: 600,
    color: '#141922', fontFamily: "'Barlow', sans-serif",
  },
  jugadorCelular: { fontSize: 11, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif" },
  sinJugadores: {
    fontSize: 13, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif",
    textAlign: 'center', padding: '16px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  },
  linkNomina: { color: '#1A6BFF', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  modalBotones: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 },
  btnCancelarModal: {
    padding: '12px', background: 'white', border: '1.5px solid #E8ECF4',
    borderRadius: 12, fontSize: 14, fontWeight: 700, color: '#6B7280',
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
  },
  btnEnviarWsp: {
    padding: '12px', background: '#25D366', border: 'none', borderRadius: 12,
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 16, fontWeight: 800, color: 'white', cursor: 'pointer',
  },
};
